<template>
  <section class="panel timeline-panel">
    <div class="panel-toolbar">
      <h2 class="panel-title">{{ getTimelineTitle() }}</h2>
    </div>
    <ol class="trail">
      <li
        v-for="(event, index) in events"
        :key="index"
        class="trail__step"
        :class="['tone-' + event.tone, { 'is-note': event.kind === 'data-changed' || event.kind === 'comment' }]"
      >
        <span class="trail__dot" aria-hidden="true"></span>
        <div class="trail__body">
          <StatusChip :status="event.status" small />
          <div class="trail__who">{{ event.user }}</div>
          <div v-if="event.text" class="trail__comment">
            <span v-if="event.kind === 'cancelled'" class="trail__label">Motiv: </span>{{ event.text }}
          </div>
          <ul v-if="event.changes.length" class="trail__changes">
            <li v-for="(change, cIndex) in event.changes" :key="cIndex">
              <span class="trail__field">{{ change.field }}</span>
              <span class="trail__from">{{ change.from }}</span>
              <v-icon x-small class="trail__arrow" aria-label="devine">mdi-arrow-right</v-icon>
              <span class="trail__to">{{ change.to }}</span>
            </li>
          </ul>
          <PhotoGallery v-if="event.files && event.files.length" class="trail__photos" :files="event.files" :size="56" download-name="demers" />
          <div class="trail__date">{{ event.date }}</div>
        </div>
      </li>
    </ol>
  </section>
</template>

<script>
import StatusChip, { statusTone } from "@/components/elements/StatusChip.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import { STAGES_005, STAGES_008 } from "@/config/periferice";

// the server writes item changes as "Model: A → B; Locația: X → Y" on the last line of the comment
const CHANGE_FIELDS = "Model|Nr\\. inventar|Locația|Cauza";
const CHANGE_SPLIT = new RegExp(`; (?=(?:${CHANGE_FIELDS}): )`);
const CHANGE_ITEM = new RegExp(`^(${CHANGE_FIELDS}): ([\\s\\S]*?) → ([\\s\\S]*)$`);

function parseChanges(line) {
  const parts = line.split(CHANGE_SPLIT).map((part) => part.match(CHANGE_ITEM));
  if (!parts.length || parts.some((match) => !match)) return null;
  return parts.map(([, field, from, to]) => ({ field, from, to }));
}

// Corrections, cancellations and data changes are not approval steps: rows carry statusId, older callers only the text
function kindOf(event) {
  const id = Number(event.statusId);
  const s = (event.status || "").toLowerCase();
  if (id === STAGES_005.CORRECTED || s.startsWith("corectat")) return "corrected";
  if (id === STAGES_005.CANCELLED || s.startsWith("anulat")) return "cancelled";
  if (id === STAGES_005.DATA_CHANGED || s.startsWith("date modificate")) return "data-changed";
  if (id === STAGES_008.COMMENT || s.startsWith("comentariu")) return "comment";
  return "step";
}

// The road a request took: one step per decision, newest last (comments and data changes in between, hollow)
export default {
  components: { StatusChip, PhotoGallery },
  props: {
    timelineData: {
      type: Array,
      required: true,
    },
  },
  computed: {
    events() {
      return this.timelineData.map((event) => {
        const kind = kindOf(event);
        let text = event.comments || "";
        let changes = [];
        if (kind === "corrected" || kind === "data-changed") {
          const lines = text.split("\n");
          const parsed = parseChanges(lines[lines.length - 1].trim());
          if (parsed) {
            changes = parsed;
            text = lines.slice(0, -1).join("\n").trim();
          }
        }
        return { ...event, kind, text, changes, tone: statusTone(event.status) };
      });
    },
  },
  methods: {
    getTimelineTitle() {
      const number = this.timelineData[0] && this.timelineData[0].number;
      return number ? `Demersul nr. ${number}` : "Drumul demersului";
    },
  },
};
</script>

<style scoped>
.trail {
  list-style: none;
  margin: 0;
  padding: 18px 20px 8px;
}
.trail__step {
  position: relative;
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 12px;
  padding-bottom: 18px;
}
.trail__step:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 9px;
  top: 20px;
  bottom: 0;
  width: 2px;
  background: var(--line);
}
.trail__dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px currentColor;
  background: currentColor;
  margin-top: 2px;
}
/* a data change does not move the request: hollow dot */
.trail__step.is-note .trail__dot {
  background: #fff;
}
.tone-sky { color: var(--sky); }
.tone-teal { color: var(--teal); }
.tone-amber { color: var(--amber); }
.tone-green { color: var(--green); }
.tone-brick { color: var(--brick); }
.tone-slate { color: var(--slate); }
.tone-plain { color: var(--ink-3); }
.tone-plum { color: var(--plum); }
.trail__body {
  color: var(--ink);
  min-width: 0;
}
.trail__who {
  margin-top: 6px;
  font-weight: 700;
}
.trail__comment {
  margin-top: 2px;
  color: var(--ink-2);
  white-space: pre-line;
  word-break: break-word;
}
.trail__label {
  font-weight: 700;
  color: var(--ink);
}
.trail__changes {
  list-style: none;
  margin: 6px 0 2px;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--mint-50);
  border: 1px solid var(--line-soft);
  color: var(--ink);
  font-size: 0.92rem;
}
.trail__changes li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
  word-break: break-word;
}
.trail__changes li + li {
  margin-top: 4px;
}
.trail__field {
  font-weight: 700;
}
.trail__field::after {
  content: ":";
}
.trail__from {
  color: var(--ink-3);
  text-decoration: line-through;
}
.trail__arrow.v-icon {
  color: var(--ink-3);
  align-self: center;
}
.trail__to {
  font-weight: 600;
}
.trail__photos {
  margin-top: 6px;
}
.trail__date {
  margin-top: 2px;
  font-size: 0.85rem;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
</style>
